import { z } from "zod";

/**
 * Schema das configurações da agenda por organização.
 * Armazenado em Organization.agendaSettings (JSONB). Null = usa defaults.
 */

/** Cor em hex válido (#RRGGBB). */
const hexColor = z
  .string()
  .regex(/^#[0-9A-Fa-f]{6}$/, "Cor deve ser hex no formato #RRGGBB");

/** Horário HH:MM válido. */
const timeString = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, "Formato HH:MM inválido");

// Os "tipos de procedimento" viviam aqui (procedureTypes) até 2026-08 — foram fundidos com
// OfferedService num catálogo único. Cor/duração agora são colunas do serviço, ver
// packages/schemas/src/service/service.ts.

export const agendaSettingsSchema = z.object({
  /** Horário inicial do expediente — também onde o grid da agenda começa (default 07:00). */
  startHour: timeString.default("07:00"),
  /** Horário final do expediente — também onde o grid da agenda termina (default 21:00). */
  endHour: timeString.default("21:00"),

  /** Duração visual de cada slot no grid (em minutos). */
  slotDuration: z.enum(["15", "30", "60"]).default("15"),
  /** Duração padrão de uma nova consulta ao criar. */
  defaultAppointmentDuration: z.number().int().min(5).max(480).default(30),

  /** Dias da semana ativos (0=dom, 1=seg, ..., 6=sáb). */
  workingDays: z
    .array(z.number().int().min(0).max(6))
    .default([1, 2, 3, 4, 5, 6]),
  /** Mostrar fins de semana no grid (mesmo se não ativos). */
  showWeekends: z.boolean().default(true),

  /** Horário de almoço (bloqueio visual no grid). Null = sem almoço fixo. */
  lunchBreak: z
    .object({
      start: timeString,
      end: timeString,
    })
    .nullable()
    .default(null),

  /** Estilo global do card de consulta. */
  cardStyle: z
    .enum([
      "colorful",
      "light",
      "assorted",
      "bordered",
      "solid",
      "minimal",
      "outlined",
      "sticker",
      "bordered-thin",
      "bordered-strong",
      "glass",
      "ribbon",
    ])
    .default("colorful"),
  /** Linhas do grid. Retrocompat: boolean true→"subtle", false→"off". */
  gridLines: z
    .preprocess(
      (v) => (typeof v === "boolean" ? (v ? "subtle" : "off") : v),
      z.enum(["off", "subtle", "strong"]),
    )
    .default("subtle"),
  /** Linha vermelha do horário atual. */
  timeLineMode: z.enum(["all", "today", "off"]).default("all"),
  /** Estilo visual da linha do horário atual. */
  timeLineStyle: z.enum(["solid", "dashed"]).default("solid"),
  /** Cor da linha do horário atual. */
  timeLineColor: hexColor.default("#F43F5E"),
  /** Destaca a linha do dia atual com traço mais grosso + dot. Se false, todas iguais. */
  timeLineHighlightToday: z.boolean().default(true),

  /** Default do toggle "Lembrete WhatsApp" ao criar consulta. */
  whatsappReminderDefault: z.boolean().default(true),
  /** Quando enviar o lembrete. */
  reminderTime: z.enum(["1h", "12h", "24h", "48h"]).default("24h"),
  /** Template da mensagem. Placeholders: {nome}, {data}, {hora}, {procedimento}, {clinica}. */
  reminderTemplate: z
    .string()
    .trim()
    .max(500)
    .default(
      "Olá {nome}! Lembrete: sua consulta de {procedimento} está marcada para {data} às {hora} na {clinica}.",
    ),

  /** Comportamento ao tentar criar conflito. */
  conflictBehavior: z.enum(["warn", "block"]).default("warn"),
  /** Pedir confirmação ao arrastar consulta pra outro horário. */
  confirmOnDrag: z.boolean().default(true),

  /** Configuração por profissional — cor e prefixo (Dr./Dra./Prof./Enf.). */
  dentistSettings: z
    .record(
      z.string(),
      z.object({
        color: hexColor.optional(),
        title: z.enum(["Dr.", "Dra.", "Prof.", "Enf.", ""]).optional(),
        /** Sala padrão por dia da semana ("0" = domingo) — sugestão ao marcar consulta. */
        salaPorDia: z.record(z.string(), z.string()).optional(),
      }),
    )
    .default({}),
  /** Mostra "Dr. João" ou "Dra. Maria" dentro do card de consulta. */
  showDentistInCard: z.boolean().default(false),
  /** Mostra intervalo "08:00 → 08:30" antes do nome do paciente. */
  showTimeInCard: z.boolean().default(false),
});

export type AgendaSettings = z.infer<typeof agendaSettingsSchema>;

/**
 * O EXPEDIENTE — quando a organização está aberta.
 *
 * Mora no mesmo JSON por razão histórica (nasceu como configuração da agenda), mas não é
 * preferência de tela: é fato da organização, do mesmo naipe que endereço e telefone, e hoje é lido
 * por mais de um lugar — a grade da Agenda e a situação de cada profissional na Equipe. Por isso é
 * editado em Configurações → Organização, e por isso tem input próprio: quem edita o expediente não
 * vê lembrete nem estilo de card, e mandar o objeto inteiro de lá sobrescreveria o que não está na
 * tela.
 */
export const expedienteSchema = z.object({
  startHour: timeString,
  endHour: timeString,
  workingDays: z.array(z.number().int().min(0).max(6)),
  lunchBreak: z.object({ start: timeString, end: timeString }).nullable(),
});
export type Expediente = z.infer<typeof expedienteSchema>;

/** Gera defaults aplicando o schema (equivalente a parse({})). */
export function defaultAgendaSettings(): AgendaSettings {
  return agendaSettingsSchema.parse({});
}
